import { get } from 'lodash-es';
import slugify from 'slugify';

import { getNewYorkDateTime } from '../../../utils/format-date';
import {
  ExpressPriceRanges,
  Flow,
  RinkChaletPriceRanges,
  RinkPremiumPriceRanges,
  RinkPriceRanges,
  StatGroup,
  TheBeamPackagePriceRanges,
  TorGaPriceRanges,
  AllInPassPriceRanges,
} from '../constants';

import { isBosProduct } from '.';

// import { getNewYorkDateTime } from '../../../utils/format-date';

// import { isBosProduct } from '.';

import type { Availability } from '../../../components/BuyTickets/SegmentedTimePicker/Availabilities';
import type { TicketType } from '../types';
import type { BOSProduct, PerformanceResponseObject } from '../types/flows';
import type { ProductTicketType } from '../types/response';

type Keys = 'DAY' | 'EVENT' | 'PRODUCT';

const responseMap = (data: any, key: Keys) => {
  const mappedData = get(data, `data.${key}LIST.${key}`);
  return { status: data.data.status, [`${key.toLowerCase()}s`]: mappedData };
};

const responseMapData = (data: any) => {
  const mappedData = get(data, 'data');

  return { status: data.data.status, ...mappedData };
};

const dataProviderResponseMap = (data: any) => {
  return get(data, `data.DATAPROVIDER.ITEMLIST.ITEM`);
};

const readAccountCategoryByAKResponseMap = (data: any) => {
  const mappedData = get(
    data,
    'data.DMGCATEGORY.MASKLIST.MASK.FIELDLIST.FIELD',
  );
  return { status: data.data.status, ...mappedData };
};

const saveAccountResponseMap = (data: any) => {
  if (!validateResponse(data)) return data;
  const account = get(data, 'data.BASICINFO');
  const ERROR = get(data, 'data.ERROR');
  const ERRORLISTOBJTYPE = get(data, 'data.ERRORLISTOBJTYPE');
  return { status: data.data.status, ...account, ERROR, ERRORLISTOBJTYPE };
};

export const searchAccountResponseMap = (data: any) => {
  if (!validateResponse(data)) return data;
  const accountResults = putInArray(get(data, 'data.ACCOUNTLIST.ACCOUNT'));
  const account = accountResults[0] ? accountResults.pop() : [];
  return { status: data.data.status, ...data, ...account };
};

// BOS sends newyork time as UTC time
// This function offset to return correct Zulu time
// const correctUTCTime = (day: string, time: string) => {
//   const datetime = `${day}T${time}`;

//   return getNewYorkDateTime(datetime).toISOString();
// };

// EXAMPLE FINDALLPERFORMANCEBYCATEGORIES Response:
// <PERFORMANCELIST>
//   <PERFORMANCE>
//       <ID>270528</ID>
//       <AK>TOR.EVN3.PRF153687</AK>
//       <REACHABLE>true</REACHABLE>
//       <TRANSFERRABLE>false</TRANSFERRABLE>
//   </PERFORMANCE>
//   <PERFORMANCE>
//       <ID>270529</ID>
//       <AK>TOR.EVN3.PRF153688</AK>
//       <REACHABLE>true</REACHABLE>
//       <TRANSFERRABLE>false</TRANSFERRABLE>
//   </PERFORMANCE>
// </PERFORMANCELIST>

const findAllPerformanceByCategoriesResponseMap = (data: any) => {
  const performances = get(data, 'data.PERFORMANCELIST.PERFORMANCE');
  return putInArray(performances);
};

const calendarAvailabilityMap = (data: any) => {
  const daysData = get(data, 'data.DAYLIST.DAY') ?? [];

  const days = putInArray(daysData);
  const daysMap = new Map<string, { DATE: string; available: boolean }>();
  days.forEach((day: any) => {
    // Can't use the sliced date as key, if we are dealing with multiple months
    // hence full date as key for the map
    const fullDate = day.DATE;
    daysMap.set(fullDate, {
      DATE: fullDate,
      available: parseInt(day.AVAILABILITY.AVAILABLE) > 0,
    });
  });

  return daysMap;
};

const mapDayPerformances = (data: any) => {
  const date = get(data, 'data.DATE') ?? '';
  const performanceData = get(data, 'data.PERFORMANCELIST.PERFORMANCE', null);
  const performances = performanceData ? putInArray(performanceData) : [];

  const perfList = performances.map((p) => {
    const splitDate = p?.TIME.split(':');
    const hours = parseInt(splitDate[0]);
    return {
      ...p,
      DATE: date,
      TIME: `${date}T${p.TIME}`,
      RAW_TIME: {
        hours: hours,
        minutes: splitDate[1],
        meridian: parseInt(splitDate[0]) >= 12 ? 'PM' : 'AM',
      },
    };
  });

  return perfList as PerformanceResponseObject[];
};

const validateResponse = (data: any) => !get(data, `data.ERRORLISTOBJTYPE`);

const putInArray = (item: any) => (Array.isArray(item) ? item : [item]);

/**
 *
 * @param availability Availability
 * @returns boolean
 * TODO: Swap to dynamic rather than static check when/if possible.
 * This must be replaced with a non-static check to see if a performance/availabilty
 * should sell daytime or sunset tickets. This implementation is currently unsafe b/c the
 * price table AK values could change. Right now referencing the price table AK is the only
 * way to discern if a performance is sunset or not.
 * These are the two price table AKs currently:
 * DAYTIME  - C435CDC3AD044A988F2B020B3D1D2024
 * SUNSET   - 6D4DC4ACE67141939ABC5D8D8DD50BF9
 */
const isSunsetAvailability = (availability: Availability) =>
  availability.priceTableAK === '6D4DC4ACE67141939ABC5D8D8DD50BF9';

const isSunsetProduct = (p: BOSProduct) =>
  putInArray(p.STATGROUPLIST.STATGROUP)
    .map((list) => list.CODE)
    .includes('SUNSET');

const types = [
  'adult',
  'child',
  'senior',
  'toddler',
  'chaperone',
  'vip',
  'chalet',
];

const transformToGeneric = (type: string) =>
  type === 'vip' || type === 'chalet-reservation';

const typesHash = Object.fromEntries(
  types.map((type) => [type, transformToGeneric(type) ? 'generic' : type]),
);

enum RinkMembershipTypes {
  SeasonMember = 'season-member',
  SeasonMemberLocker = 'season-member-and-locker',
  SeasonMemberSkateRental = 'season-member-w-skate-rental',
  SeasonMemberSkateAndLocker = 'season-member-and-locker-and-skates',
}

const addType = (_name: string) => {
  const name = slugify(_name.toLowerCase());

  if (name.includes('season-member')) {
    // Rink Membership Types are similiar, `.includes()` will trip on any of them
    // Being more explicit with === here
    for (const key of Object.keys(RinkMembershipTypes)) {
      const value =
        RinkMembershipTypes[key as keyof typeof RinkMembershipTypes];

      if (value === name) return value;
    }
  } else {
    for (const type of Object.keys(typesHash)) {
      if (name.includes(type)) {
        return typesHash[type];
      }
    }
  }

  return '';
};

// CAUSES STRANGE ERROR
const sanitizeProductsToTicketTypes = (
  products: BOSProduct[],
): TicketType[] => {
  let productList = products;
  if (!Array.isArray(products)) {
    // TODO : isBosProduct throws an error that is hard to diagnose
    if (typeof products === 'object' && isBosProduct(products)) {
      productList = [products];
    } else {
      return [];
    }
    if (typeof products === 'object') {
      productList = [products];
    } else {
      return [];
    }
  }

  return productList
    .filter((p) => !isSunsetProduct(p))
    .map(productToTicketType)
    .sort((a, b) => types.indexOf(a.type) - types.indexOf(b.type));
};

export const sanitizeAndSortProductsToTicketTypes = (
  products: BOSProduct[],
  flow?: Flow,
): ProductTicketType[] => {
  let productList = products;
  if (!Array.isArray(products)) {
    if (typeof products === 'object' && isBosProduct(products)) {
      productList = [products];
    } else {
      return [];
    }
  }

  return sortedProductsByType(productList, flow);
};

function calculatePriceRange(product: BOSProduct) {
  const statGroup = putInArray(product.STATGROUPLIST.STATGROUP).map(
    (list) => list.CODE,
  )[0];

  if (statGroup === StatGroup.RINK_VIP) {
    return RinkPremiumPriceRanges;
  } else if (
    statGroup === StatGroup.RINK_ADULT ||
    statGroup === StatGroup.RINK
  ) {
    return RinkPriceRanges.adult;
  } else if (statGroup === StatGroup.RINK_CHILD) {
    return RinkPriceRanges.child;
  } else if (statGroup === StatGroup.RINK_TODDLER) {
    return RinkPriceRanges.toddler;
  } else if (statGroup === StatGroup.RINK_CHAPERONE) {
    return RinkPriceRanges.chaperone;
  } else if (statGroup === StatGroup.CHALETS) {
    return RinkChaletPriceRanges;
  } else if (statGroup === StatGroup.TOR_XPASS) {
    return ExpressPriceRanges;
  } else if (statGroup === StatGroup.GA_ADULT) {
    return TorGaPriceRanges.adult;
  } else if (statGroup === StatGroup.GA_CHILD) {
    return TorGaPriceRanges.child;
  } else if (statGroup === StatGroup.GA_SENIOR) {
    return TorGaPriceRanges.senior;
  } else if (statGroup === StatGroup.ALLINPSKYA) {
    return AllInPassPriceRanges.adult;
  } else if (statGroup === StatGroup.ALLINPSKYC) {
    return AllInPassPriceRanges.child;
  } else if (statGroup === StatGroup.ALLINPSKYS) {
    return AllInPassPriceRanges.senior;
  } else if (statGroup === StatGroup.THE_BEAM_PASS) {
    return {
      high: TheBeamPackagePriceRanges.high,
      low: TheBeamPackagePriceRanges.low,
    };
  }

  const price = parseFloat(product.PRICE.GROSS) * 100;
  return {
    high: price,
    low: price,
  };
}

function productToTicketType(product: BOSProduct) {
  const price = parseFloat(product.PRICE.GROSS) * 100;
  const taxRate = parseFloat(product.PRICE.TAXLIST?.TAX.RATE ?? 0) / 100;
  const statGroup = putInArray(product.STATGROUPLIST.STATGROUP).map(
    (list) => list.CODE,
  );

  const ticket = {
    label: product.NAME,
    statGroup,
    helpLabel: product.DESCRIPTION,
    price: parseFloat(product.PRICE.GROSS) * 100,
    productAK: product.AK,
    pricetableAK: product.PRICETABLE.AK,
    type: addType(product.NAME),
    tax: Math.ceil(price * taxRate),

    // TODO: Correct price range
    priceRange: calculatePriceRange(product),
  };

  return ticket;
}

const productWithVariantsToTicketType = (product: BOSProduct) => {
  const price = parseFloat(product.PRICE.GROSS) * 100;
  const taxRate = parseFloat(product.PRICE.TAXLIST?.TAX.RATE ?? 0) / 100;

  const ticket = {
    label: product.NAME,
    statGroup: putInArray(product.STATGROUPLIST.STATGROUP).map(
      (list) => list.CODE,
    ),
    helpLabel: product.DESCRIPTION,
    price: price,
    productAK: product.AK,
    pricetableAK: product.PRICETABLE.AK,
    type: addType(product.NAME),
    tax: Math.ceil(price * taxRate),
    prices: [price],
    priceRange: calculatePriceRange(product),
    variants: [],
  };

  return ticket;
};

const parseFloatToCents = (price: string) => {
  return Math.round((parseFloat(price) + Number.EPSILON) * 100);
};

const sortedProductsByType = (_products: BOSProduct[], flow?: Flow) => {
  const products = _products.sort(
    (a, b) => parseFloat(a.PRICE.GROSS) - parseFloat(b.PRICE.GROSS),
  );

  if (!products.length) return [];

  const priceRangesByType: Record<string, string[]> = {};

  products.forEach((product) => {
    const type = addType(product.NAME);
    if (!priceRangesByType[type]) priceRangesByType[type] = [];

    const asSet = new Set(
      [...priceRangesByType[type], product.PRICE.GROSS].sort(
        (a, b) => parseInt(a) - parseInt(b),
      ),
    );
    priceRangesByType[type] = [...asSet];
  });

  const productsByType = products.reduce(
    (acc: ProductTicketType[], product: BOSProduct) => {
      const productTicket: ProductTicketType =
        productWithVariantsToTicketType(product);
      productTicket.variants.push(productToTicketType(product));
      const priceLow = priceRangesByType[productTicket.type][0];
      const priceHigh = priceRangesByType[productTicket.type].slice(-1)[0];

      let index = acc.findIndex(
        (ticket: ProductTicketType) => ticket.type === productTicket.type,
      );

      if (index !== -1) {
        acc[index].variants.push(productToTicketType(product));
        acc[index].prices.push(parseFloat(product.PRICE.GROSS) * 100);
      } else {
        acc.push(productTicket);
        index = 0;
      }

      const ticketType = addType(product.NAME);
      acc[index].prices.sort();
      if (flow === Flow.RINK) {
        const statGroup = putInArray(product.STATGROUPLIST.STATGROUP).map(
          (list) => list.CODE,
        )[0];
        if (
          statGroup === StatGroup.RINK_ADULT ||
          statGroup === StatGroup.RINK
        ) {
          acc[index].priceRange = RinkPriceRanges.adult;
        } else if (statGroup === StatGroup.RINK_CHILD) {
          acc[index].priceRange = RinkPriceRanges.child;
        } else if (statGroup === StatGroup.RINK_TODDLER) {
          acc[index].priceRange = RinkPriceRanges.toddler;
        } else if (statGroup === StatGroup.RINK_CHAPERONE) {
          acc[index].priceRange = RinkPriceRanges.chaperone;
        } else {
          acc[index].priceRange = RinkPriceRanges.adult;
        }
      } else if (flow === Flow.ALL_IN_PASS) {
        acc[index].priceRange = {
          high: AllInPassPriceRanges[ticketType].high,
          low: AllInPassPriceRanges[ticketType].low,
        };
      } else {
        acc[index].priceRange = {
          high:
            flow && (flow === Flow.TOR_GA || flow === Flow.RINK_MEMBERSHIP)
              ? TorGaPriceRanges[ticketType].high
              : flow === Flow.THE_BEAM_PASS
              ? TheBeamPackagePriceRanges.high
              : parseFloatToCents(priceHigh),
          low:
            flow && (flow === Flow.TOR_GA || flow === Flow.RINK_MEMBERSHIP)
              ? TorGaPriceRanges[ticketType].low
              : flow === Flow.THE_BEAM_PASS
              ? TheBeamPackagePriceRanges.low
              : parseFloatToCents(priceLow),
        };
      }

      return acc;
    },
    [],
  );

  return productsByType.sort(
    (a: ProductTicketType, b: ProductTicketType) =>
      types.indexOf(a.type) - types.indexOf(b.type),
  );
};

export {
  calendarAvailabilityMap,
  dataProviderResponseMap,
  findAllPerformanceByCategoriesResponseMap,
  isSunsetAvailability,
  mapDayPerformances,
  putInArray,
  readAccountCategoryByAKResponseMap,
  responseMap,
  responseMapData,
  RinkMembershipTypes,
  sanitizeProductsToTicketTypes,
  saveAccountResponseMap,
};
